import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import { useControlField, useField } from 'remix-validated-form'
import { Input } from '~/components/ui'
import FieldContainer from './FieldContainer'

export type TextFieldProps = {
  className?: string
  label?: string
  name: string
  type?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  prefix?: string
  suffix?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
  autoFocus?: boolean
  autoComplete?: string
}

const TextField = React.memo(
  React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
    const {
      type = 'text',
      name,
      disabled,
      placeholder,
      prefix,
      suffix,
      onBlur,
      autoFocus,
      autoComplete,
    } = props
    const { error, getInputProps, defaultValue, validate } = useField(name)
    const [value, setValue] = useControlField<string>(name)
    const [hasInteracted, setHasInteracted] = useState(false)

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasInteracted) {
          setHasInteracted(true)
        }
        setValue(e.target.value)
      },
      [setValue, hasInteracted]
    )

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement, Element>) => {
        if (!hasInteracted) {
          setHasInteracted(true)
        }
        onBlur?.(e)
        validate()
      },
      [onBlur, validate, hasInteracted]
    )

    // Only show modification styling after user has interacted, for memoized lease spaces field
    const isModified = hasInteracted && defaultValue && value != defaultValue
    const isNew = hasInteracted && !defaultValue && value

    return (
      <FieldContainer {...props} id={name}>
        <div className="relative">
          {prefix && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-muted-foreground sm:text-sm">{prefix}</span>
            </div>
          )}
          <Input
            ref={ref}
            readOnly={disabled}
            type={type}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            className={clsx(
              isModified && 'ring-2 ring-yellow-300 ring-offset-2',
              isNew && 'ring-2 ring-green-400 ring-offset-2',
              prefix && 'pl-7',
              suffix && 'pr-10'
            )}
            {...getInputProps({
              id: name,
              placeholder: placeholder,
              value: value || '',
              onChange: handleChange,
              onBlur: handleBlur,
            })}
          />
          {suffix && (
            <div className="pointer-events-none absolute inset-y-0 right-0 z-0 flex items-center pr-3">
              <span className="text-muted-foreground sm:text-sm">{suffix}</span>
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </FieldContainer>
    )
  })
)

TextField.displayName = 'TextField'

export default TextField
